import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
    diagnosticsPath,
    DIAGNOSTICS_REQUEST_TTL,
    initialControlState,
    NO_AUDIO_DEVICES,
    outputSelectionSupported,
    prepareSelfCapture,
    storeSuspendedShare,
    takeSuspendedShare,
    unlockSupportCues,
    voiceGain,
    voiceSettingsOf,
    watchAudioDevices,
    type ConnectionQuality,
    type IAudioDevices,
    type IVoiceVolume,
    type IRemoteControlState,
    type ISupportDiagnostics,
    type ISupportDiagnosticsRequest,
    type ISuspendedShare,
} from '@srm/screen-sharing/light';
import type {
    IScreenShareAccess,
    IScreenShareConnectionState,
    IScreenShareOptions,
    IScreenShareParticipant,
    IScreenShareStartWith,
    IUserGestureRequest,
    ScreenShareClient,
    ScreenShareTransport,
} from '@srm/screen-sharing';
import type { ISupportActionFeedEntry } from '@molecules/SupportActionFeed';
import { createEmitter } from '@srm/ui/lib/valueStore';
import $api, { API_URL } from '@/http';
import { setPublishingShare } from '@/http/supportShareHeader';
import { auth } from '@/auth';
import { buildUserContext } from './supportUserContext';
import { createPresenceBus, SUPPORT_CAPTURE_CLASS, supportCaptureRoot } from './supportPresence';
import { emptySupportChat, type ISupportChatMessage, type ISupportChatState } from './supportChat';
import { createMicLevel, createSpeaking, initialSupportVoice, type ISupportVoiceState } from './supportVoice';
import { supportCue } from './supportCueSettings';
import { supportVoicePreferences, type ISupportSound } from './supportSound';
import { sendNotification } from '@srm/ui/lib/notifications';
import {
    feedbackRejection,
    SUPPORT_FEEDBACK_EXPIRED_TEXT,
    SUPPORT_FEEDBACK_RETRIES,
    SUPPORT_FEEDBACK_RETRY_DELAY,
    SUPPORT_FEEDBACK_THANKS_TIMEOUT,
    SUPPORT_FEEDBACK_TIMEOUT,
    SupportFeedbackError,
    type ISupportFeedbackOffer,
} from './supportFeedback';
import { SupportAccessService } from '@services/SupportAccessService';
import type { SupportFeedbackRating } from '@models/supportAccess/supportAccess.types';
import type { RootState } from '@/store';
import { onSupportIssue } from '@utils/supportErrorLog';
import { collectSupportDiagnostics } from './supportDiagnostics';

/** Лента действий хранит последние записи показа. */
export const SUPPORT_FEED_LIMIT = 50;
/** Ошибки страницы в контексте для поддержки — за столько до начала показа и во время него. */
export const SUPPORT_ERRORS_BEFORE_SHARE = 10 * 60 * 1000;

/** Рабочая область оболочки: сотрудник может обрезать показ до неё (Region Capture). */
export const supportCropTarget = (): Element | null => document.querySelector('.main-content');

/**
 * Выбор вкладки для показа (§40): только вкладка браузера — экраны в выборе не предлагаются, переключиться на другую
 * поверхность посреди показа нельзя; видео без звука, частота кадров — под текст.
 */
const CAPTURE_OPTIONS = {
    video: { frameRate: { ideal: 10, max: 15 }, displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    monitorTypeSurfaces: 'exclude',
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
};

/** Выбрали весь экран или окно, а не вкладку (§40): показ не начинается. */
export const SUPPORT_TAB_ONLY_TEXT = 'Покажите вкладку кабинета, а не весь экран или окно';

/** Захвачена вкладка браузера; браузер, который не называет поверхность, — не повод отказать. */
const isTabCapture = (stream: MediaStream) => {
    const surface = stream.getVideoTracks()[0]?.getSettings?.().displaySurface;
    return !surface || surface === 'browser';
};

/** «Продолжить показ» не получилось, а показ ещё ждёт (§35). */
export const SUPPORT_RESUME_FAILED_TEXT = 'Не удалось продолжить показ. Попробуйте ещё раз.';

/** Запрос диагностики у пользователя: `sending` — пакет уходит, `failed` — не ушёл, можно повторить. */
export interface ISupportDiagnosticsAsk extends ISupportDiagnosticsRequest {
    phase: 'asking' | 'sending' | 'failed';
    error?: string;
    /** Когда плашка уходит (мс): срок сервера `expiresAt` или первое появление + `DIAGNOSTICS_REQUEST_TTL` */
    deadline?: number;
}

interface IScreenShareState {
    /** Идёт показ (от выбора вкладки до остановки) */
    active: boolean;
    status: string;
    /** Окончательный отказ — показ остановлен */
    error: string;
    control: IRemoteControlState;
    /** Состояние связи; `null` — показа нет */
    connection: IScreenShareConnectionState | null;
    /** Подключившийся сотрудник поддержки (последний из подключившихся) */
    viewerName: string | null;
    /** Все сотрудники, которые смотрят экран: имя, цвет участника, кто управляет */
    participants: IScreenShareParticipant[];
    /** На экране есть пометки сотрудников — в полосе кнопка «Убрать пометки» */
    annotated: boolean;
    /** Действия сотрудника в кабинете за показ (лента) */
    actions: ISupportActionFeedEntry[];
    /** Качество канала по статистике; `null` — видео ещё не идёт */
    quality: ConnectionQuality | null;
    /** Захват ограничен корнем кабинета (Element Capture): оверлеи поддержки в видео не попадают */
    restricted: boolean;
    /**
     * Показ обрезан до рабочей области (Region Capture): Element Capture на это время снят, всё, что нарисовано поверх
     * области, попадает в видео
     */
    cropped: boolean;
    /** Видео (`webrtc`) или снимки через сервер (`snapshots`), когда видеосвязь не установилась */
    transport: ScreenShareTransport;
    /**
     * Область кадра для пересчёта координат: рабочая область при обрезке, корень захвата при Element Capture;
     * `null` — окно (захват не ограничен или кадр не совпал с корнем по пропорциям)
     */
    captureArea: (() => Element | null) | undefined;
    /** Чат показа (IMPLEMENTATION.md §30) */
    chat: ISupportChatState;
    /** Голос показа (§31) */
    voice: ISupportVoiceState;
    /** Показ закончился при участии специалистов — плашка оценки (§33); `null` — оценивать нечего */
    feedback: ISupportFeedbackOffer | null;
    /**
     * Показ приостановлен: страница обновилась, захвата нет, чат работает, специалисты ждут «Продолжить показ» (§35)
     */
    suspended: boolean;
    /** «Продолжить показ» нажато: браузер спрашивает вкладку или сервер возвращает показ */
    resuming: boolean;
    /**
     * Показ после перезагрузки ещё возвращается (клиент грузится, сервер не ответил): «Продолжить показ» пока ничего не
     * сделал бы — кнопка ждёт
     */
    restoring: boolean;
    /** «Продолжить показ» не получилось — показ по-прежнему ждёт */
    resumeError: string;
    /** Показ не начат: выбрали не вкладку (§40) — в полосе ошибки «Выбрать заново» */
    reselect: boolean;
    /** Пометки сотрудников разрешены; «Запретить пометки» в меню полосы (§40) */
    annotationsAllowed: boolean;
    /** Команда сотрудника, которую браузер выполнит только по нажатию пользователя (§40); `null` — нет */
    gesture: IUserGestureRequest | null;
    /** Специалист просит собрать диагностику (§43) — плашка согласия; `null` — не просит */
    diagnostics: ISupportDiagnosticsAsk | null;
}

/** Когда эта вкладка впервые увидела запрос диагностики — переживает перезагрузку (запрос приходит снова с `Resume`). */
const DIAGNOSTICS_SEEN_KEY = 'srm.screenShare.diagnosticsSeen';

function diagnosticsFirstSeen(requestId: string, now = Date.now()): number {
    try {
        const raw: unknown = JSON.parse(sessionStorage.getItem(DIAGNOSTICS_SEEN_KEY) ?? '{}');
        const seen = Object.fromEntries(
            Object.entries(raw && typeof raw === 'object' ? raw : {}).filter(
                (entry): entry is [string, number] =>
                    typeof entry[1] === 'number' && now - entry[1] < DIAGNOSTICS_REQUEST_TTL,
            ),
        );
        const at = seen[requestId] ?? now;
        seen[requestId] = at;
        sessionStorage.setItem(DIAGNOSTICS_SEEN_KEY, JSON.stringify(seen));
        return at;
    } catch {
        return now;
    }
}

/**
 * Срок запроса: свой срок сервера, если он его прислал (`expiresAt`, расширение §43 — необязательное поле), иначе
 * первое появление в этой вкладке + `DIAGNOSTICS_REQUEST_TTL`.
 */
function diagnosticsExpiry(request: ISupportDiagnosticsRequest): number {
    const parsed = request.expiresAt ? Date.parse(request.expiresAt) : NaN;
    return Number.isFinite(parsed) ? parsed : diagnosticsFirstSeen(request.requestId) + DIAGNOSTICS_REQUEST_TTL;
}

/**
 * «Завершить» до загрузки клиента возвращённого показа (§35): клиент — только чтобы вернуть показ на сервере и сразу
 * закончить его (`Leave`), иначе специалисты ждали бы «приостановлен» до конца срока. Без входа (выход из учётной
 * записи) показ кончается по сроку на сервере.
 */
function endSuspendedShare(
    sharing: typeof import('@srm/screen-sharing'),
    saved: ISuspendedShare,
    options: Omit<IScreenShareOptions, 'controlHost' | 'restrictTarget' | 'startWith' | 'resumeShare'>,
): void {
    if (!auth.pageAccessToken()) return;
    const ending = new sharing.ScreenShareClient({ ...options, persistPublisher: false, resumeShare: saved });
    const end = () => void ending.dispose();
    void ending.connect().then(end, end);
}

/** Выбранная вкладка, готовая к показу (§40): захват, корень захвата и клиентский модуль. */
interface ITabCapture {
    stream: MediaStream;
    handle: ReturnType<typeof prepareSelfCapture>;
    sharing: typeof import('@srm/screen-sharing');
    /** Захват ограничен корнем страницы (Element Capture) */
    restricted: boolean;
    release: () => void;
}

/** Пакет не ушёл (сеть, сервер): плашка остаётся, «Отправить» — ещё раз. */
export const SUPPORT_DIAGNOSTICS_FAILED_TEXT = 'Не удалось отправить сведения. Попробуйте ещё раз.';
/** Запрос уже ответили или он истёк (409, 404): плашка уходит. */
export const SUPPORT_DIAGNOSTICS_STALE_TEXT = 'Запрос сведений уже неактуален';
export const SUPPORT_DIAGNOSTICS_SENT_TEXT = 'Технические сведения отправлены сотруднику поддержки';

const httpStatus = (reason: unknown) => (reason as { response?: { status?: number } } | undefined)?.response?.status;
/** Текст отказа сервера (`{ message }`): 403 — во время управления отправляет только сам пользователь, 429 — часто. */
const httpMessage = (reason: unknown) => {
    const message = (reason as { response?: { data?: { message?: unknown } } } | undefined)?.response?.data?.message;
    return typeof message === 'string' && message.trim() ? message.trim() : undefined;
};

/** Без кто-говорит: он меняется несколько раз в секунду и живёт в своём сторе (`speaking`). */
const voiceWithoutSpeaking = (voice: ISupportVoiceState): ISupportVoiceState =>
    voice.speaking.length ? { ...voice, speaking: initialSupportVoice.speaking } : voice;

/**
 * Показ своей вкладки поддержке. Живёт в `ScreenShareProvider`: переживает перемонтирование оболочки. `owner` — id
 * вошедшего пользователя: приостановленный показ (§35) достаётся после перезагрузки только ему.
 */
export function useScreenShare(owner?: string, readState?: () => RootState) {
    const client = useRef<ScreenShareClient | null>(null);
    const ownerRef = useRef(owner);
    ownerRef.current = owner;
    /** Запись приостановленного показа, пока клиент для неё ещё грузится (§35): уход со страницы вернёт её на место. */
    const pendingRestore = useRef<ISuspendedShare | null>(null);
    /**
     * «Завершить» нажато, пока клиент возвращённого показа ещё грузился: клиента, которому сказать `Leave`, нет — его
     * создадут после загрузки только для этого.
     */
    const endAfterLoad = useRef<ISuspendedShare | null>(null);
    const generation = useRef(0);
    const actionId = useRef(0);
    const [active, setActive] = useState(false);
    const [status, setStatus] = useState('');
    const [error, setError] = useState('');
    const [control, setControl] = useState<IRemoteControlState>(initialControlState);
    const [connection, setConnection] = useState<IScreenShareConnectionState | null>(null);
    const [viewerName, setViewerName] = useState<string | null>(null);
    const [participants, setParticipants] = useState<IScreenShareParticipant[]>([]);
    const [annotated, setAnnotated] = useState(false);
    const [actions, setActions] = useState<ISupportActionFeedEntry[]>([]);
    const [quality, setQuality] = useState<ConnectionQuality | null>(null);
    const [restricted, setRestricted] = useState(false);
    const [cropped, setCropped] = useState(false);
    const [transport, setTransport] = useState<ScreenShareTransport>('webrtc');
    const [captureArea, setCaptureArea] = useState<(() => Element | null) | undefined>(undefined);
    const [presence] = useState(createPresenceBus);
    const [chat, setChat] = useState<ISupportChatState>(emptySupportChat);
    const [voice, setVoice] = useState<ISupportVoiceState>(initialSupportVoice);
    const [feedback, setFeedback] = useState<ISupportFeedbackOffer | null>(null);
    const [suspended, setSuspended] = useState(false);
    const [resuming, setResuming] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [resumeError, setResumeError] = useState('');
    const [reselect, setReselect] = useState(false);
    /** С чем начинался показ, для которого выбрали не вкладку: «Выбрать заново» начинает его так же. */
    const reselectWith = useRef<IScreenShareStartWith | undefined>(undefined);
    const [annotationsAllowed, setAnnotationsAllowedState] = useState(true);
    const [gesture, setGesture] = useState<IUserGestureRequest | null>(null);
    const gestureRef = useRef<IUserGestureRequest | null>(null);
    // Запросы диагностики по очереди (§43): плашка — о первом, следующий ждёт ответа на него, а не вытесняет его.
    const [diagnosticsQueue, setDiagnosticsQueue] = useState<readonly ISupportDiagnosticsAsk[]>([]);
    const diagnostics = diagnosticsQueue.at(0) ?? null;
    const diagnosticsRef = useRef<ISupportDiagnosticsAsk | null>(null);
    diagnosticsRef.current = diagnostics;
    const updateDiagnostics = useCallback(
        (requestId: string, next: ISupportDiagnosticsAsk | null) =>
            setDiagnosticsQueue((queue) =>
                next
                    ? queue.map((item) => (item.requestId === requestId ? next : item))
                    : queue.filter((item) => item.requestId !== requestId),
            ),
        [],
    );
    const readStateRef = useRef(readState);
    readStateRef.current = readState;
    const [micLevel] = useState(() => createMicLevel());
    const [speaking] = useState(() => createSpeaking());
    const voiceKey = useRef('');
    const [incoming] = useState(() => createEmitter<ISupportChatMessage>());
    /**
     * Специалисты показа для звуков «подключился»/«ушёл»; `null` — первый список после возврата показа (§35) берётся
     * как есть, без звука: эти специалисты были в показе и до перезагрузки.
     */
    const knownPeers = useRef<Set<string> | null>(new Set());
    // Для оценки: какой это сеанс, кто из специалистов подключался и с какого момента.
    const session = useRef<{ caseId: string | null; names: string[]; startedAt: number }>({
        caseId: null,
        names: [],
        startedAt: 0,
    });

    /** Показ этой вкладки кончился: `offerFeedback` — предложить оценку (страница остаётся, а не уходит). */
    const reset = useCallback(
        (offerFeedback: boolean) => {
            generation.current++;
            client.current = null;
            const ended = session.current;
            session.current = { caseId: null, names: [], startedAt: 0 };
            // Оценка — только если к показу кто-то подключался: показ, оборвавшийся без специалиста, оценивать нечего.
            if (offerFeedback && ended.caseId && ended.names.length)
                setFeedback({
                    caseId: ended.caseId,
                    names: ended.names,
                    minutes: Math.max(1, Math.round((Date.now() - ended.startedAt) / 60000)),
                });
            setPublishingShare(null);
            supportCaptureRoot().classList.remove(SUPPORT_CAPTURE_CLASS);
            setActive(false);
            setStatus('');
            setControl(initialControlState);
            setConnection(null);
            setViewerName(null);
            setParticipants([]);
            setAnnotated(false);
            setActions([]);
            setQuality(null);
            setRestricted(false);
            setCropped(false);
            setTransport('webrtc');
            setCaptureArea(undefined);
            setChat(emptySupportChat);
            setVoice(initialSupportVoice);
            voiceKey.current = '';
            speaking.set([]);
            setSuspended(false);
            setResuming(false);
            setRestoring(false);
            setResumeError('');
            setAnnotationsAllowedState(true);
            gestureRef.current?.dismiss();
            gestureRef.current = null;
            setGesture(null);
            setDiagnosticsQueue([]);
            micLevel.set(0);
            presence.emit({ kind: 'annotations', annotations: [] });
        },
        [presence, micLevel, speaking],
    );
    /** Голос в состоянии показа меняется только вместе с микрофонами, а не с каждым «говорит». */
    const applyVoice = useCallback((next: ISupportVoiceState) => {
        const plain = voiceWithoutSpeaking(next);
        const key = JSON.stringify(plain);
        if (key === voiceKey.current) return;
        voiceKey.current = key;
        setVoice(plain);
    }, []);
    const stop = useCallback(() => {
        // Возвращённый показ, клиент которого ещё грузится (§35): завершить его, когда загрузится, — и не сохранять
        // при уходе со страницы.
        const pending = pendingRestore.current;
        pendingRestore.current = null;
        if (!client.current && pending) endAfterLoad.current = pending;
        void client.current?.dispose();
        reset(true);
    }, [reset]);
    useEffect(
        () => () => {
            generation.current++;
            void client.current?.dispose();
            setPublishingShare(null);
            supportCaptureRoot().classList.remove(SUPPORT_CAPTURE_CLASS);
        },
        [],
    );

    /**
     * Обратные вызовы клиента, общие для нового показа и возвращённого после перезагрузки. `live` — этот клиент ещё
     * текущий: поздний вызов остановленного ничего не меняет.
     */
    const clientOptions = useCallback(
        (
            live: () => boolean,
            startedAt: number,
        ): Omit<IScreenShareOptions, 'controlHost' | 'restrictTarget' | 'startWith' | 'resumeShare'> => {
            const seen = (name: string | null | undefined) => {
                if (name && !session.current.names.includes(name)) session.current.names.push(name);
            };
            return {
                apiUrl: API_URL,
                token: () => auth.pageAccessToken() ?? '',
                access: async () =>
                    (
                        await $api.post<IScreenShareAccess>(
                            '/screenSharing/access',
                            { viewer: false },
                            {
                                suppressErrorNotification: true,
                            },
                        )
                    ).data,
                viewer: false,
                contextSource: () => buildUserContext(startedAt - SUPPORT_ERRORS_BEFORE_SHARE),
                cropTarget: supportCropTarget,
                // Обновление страницы приостанавливает показ (§35): страница после перезагрузки его вернёт — тому же
                // пользователю.
                persistPublisher: ownerRef.current ? { owner: ownerRef.current } : true,
                onShareId: (shareId) => {
                    if (!live()) return;
                    setPublishingShare(shareId);
                    if (shareId) session.current.caseId = shareId;
                },
                onSuspended: (next) => {
                    if (!live()) return;
                    setSuspended(next);
                    setRestoring(false);
                    if (!next) setResumeError('');
                },
                onCaptureChange: (capture) => {
                    if (!live()) return;
                    setCropped(capture.cropped);
                    setRestricted(capture.restricted);
                },
                onTransport: (next) => {
                    if (live()) setTransport(next);
                },
                onChat: (next) => {
                    if (live()) setChat(next);
                },
                onChatMessage: (message) => {
                    if (live()) incoming.emit(message);
                },
                onVoice: (next) => {
                    if (live()) applyVoice(next);
                },
                onSpeaking: (keys) => {
                    if (live()) speaking.set(keys);
                },
                onMicLevel: (level) => {
                    if (live()) micLevel.set(level);
                },
                // Звук (§42): выбранного микрофона или динамиков нет — взяты системные.
                voiceSettings: voiceSettingsOf(supportVoicePreferences().get()),
                onVoiceNotice: (message) => {
                    if (live()) sendNotification({ message, variant: 'yellow' });
                },
                onControl: (next) => {
                    if (live()) setControl(next);
                },
                onStatus: (message) => {
                    if (live()) setStatus(message);
                },
                onConnection: (next) => {
                    if (live()) setConnection(next);
                },
                onPeer: (peer) => {
                    if (!live()) return;
                    setViewerName(peer?.name ?? null);
                    seen(peer?.name);
                    if (!peer) setQuality(null);
                },
                onParticipants: (list) => {
                    if (!live()) return;
                    setParticipants(list);
                    list.forEach((person) => seen(person.name));
                    const ids = new Set(list.map((person) => person.peerId));
                    const known = knownPeers.current;
                    knownPeers.current = ids;
                    if (!known) return;
                    if ([...ids].some((peerId) => !known.has(peerId))) supportCue('join');
                    else if ([...known].some((peerId) => !ids.has(peerId))) supportCue('leave');
                },
                onPeerCursor: (peerId, cursor) => {
                    if (live()) presence.emit({ kind: 'cursor', cursor, peerId });
                },
                onPeerClick: (peerId, mark) => {
                    if (live()) presence.emit({ kind: 'click', mark, peerId });
                },
                onAnnotationsAllowed: (allowed) => {
                    if (live()) setAnnotationsAllowedState(allowed);
                },
                onAnnotations: (annotations) => {
                    if (!live()) return;
                    presence.emit({ kind: 'annotations', annotations });
                    setAnnotated(annotations.length > 0);
                },
                onAction: (entry) => {
                    if (!live()) return;
                    const item = { ...entry, id: ++actionId.current };
                    setActions((list) => [...list, item].slice(-SUPPORT_FEED_LIMIT));
                },
                onStats: (stats) => {
                    if (live()) setQuality(stats.quality);
                },
                // §43: тот же запрос после возврата показа (`Resume`) — та же плашка, без сброса предпросмотра; другой —
                // в очередь за ней. Срок — с первого появления (и после перезагрузки страницы).
                onDiagnosticsRequest: (request) => {
                    if (!live()) return;
                    const deadline = diagnosticsExpiry(request);
                    setDiagnosticsQueue((queue) =>
                        queue.some((item) => item.requestId === request.requestId)
                            ? queue
                            : [...queue, { ...request, phase: 'asking', deadline }],
                    );
                },
                onError: (message) => {
                    if (!live()) return;
                    // Свои причины пакета и сервера — по-русски; технический текст транспорта пользователю не нужен.
                    setError(
                        /[А-Яа-яЁё]/.test(message)
                            ? message
                            : 'Не удалось продолжить показ экрана. Проверьте соединение и попробуйте снова.',
                    );
                    stop();
                },
            };
        },
        [presence, incoming, micLevel, speaking, applyVoice, stop],
    );

    /**
     * Уход со страницы (обновление, закрытие, переход): показ не завершается, а приостанавливается (§35) — браузер
     * всё равно останавливает захват, а продолжить его после загрузки можно только по нажатию. Клиент шлёт `Suspend`
     * и оставляет показ этой вкладке после перезагрузки; закрытая вкладка закончит показ по сроку на сервере.
     */
    const suspendForUnload = useCallback(() => {
        const pending = pendingRestore.current;
        pendingRestore.current = null;
        const shareId = client.current ? session.current.caseId : null;
        if (client.current) client.current.suspend();
        // Клиент для возвращённого показа ещё грузится: запись — обратно, следующая страница его вернёт.
        else if (pending) storeSuspendedShare(pending);
        else return;
        reset(false);
        // Заголовок показа — до конца этого `pagehide`: слушатели страницы после этого (черновик карточки keepalive)
        // уходят с ним, и сервер видит в них окно управления. Вернувшийся показ (bfcache) заголовок не теряет.
        if (!shareId) return;
        setPublishingShare(shareId);
        const after = generation.current;
        setTimeout(() => {
            if (generation.current === after) setPublishingShare(null);
        });
    }, [reset]);

    /**
     * Команда сотрудника просит то, что браузер разрешает только жесту пользователя (новая вкладка, файл, буфер, полный
     * экран): плашка «Сотрудник просит: …», новая просьба заменяет прежнюю.
     */
    const offerGesture = useCallback(
        (live: () => boolean) => (request: IUserGestureRequest) => {
            if (!live()) {
                request.dismiss();
                return;
            }
            gestureRef.current?.dismiss();
            gestureRef.current = request;
            setGesture(request);
        },
        [],
    );

    /**
     * Страница после перезагрузки: вернуть приостановленный показ без захвата (§35). Чат работает сразу, специалисты
     * ждут; захват — по «Продолжить показ» (`continueShare`).
     */
    const restore = useCallback(
        async (saved: ISuspendedShare) => {
            if (client.current) return;
            if (!window.RTCPeerConnection) return;
            const current = ++generation.current;
            const live = () => current === generation.current;
            const startedAt = saved.startedAt ?? Date.now();
            pendingRestore.current = saved;
            setError('');
            setFeedback(null);
            session.current = { caseId: saved.id, names: [], startedAt };
            knownPeers.current = null;
            setActive(true);
            setSuspended(true);
            setRestoring(true);
            setStatus('Возвращаемся к показу…');
            try {
                const sharing = await import('@srm/screen-sharing');
                if (!live()) {
                    if (endAfterLoad.current === saved) {
                        endAfterLoad.current = null;
                        endSuspendedShare(
                            sharing,
                            saved,
                            clientOptions(() => false, startedAt),
                        );
                    }
                    return;
                }
                let connection: ScreenShareClient | null = null;
                const area = () => connection?.captureArea() ?? null;
                setCaptureArea(() => area);
                connection = new sharing.ScreenShareClient({
                    ...clientOptions(live, startedAt),
                    resumeShare: saved,
                });
                client.current = connection;
                pendingRestore.current = null;
                setChat(connection.chat);
                applyVoice(connection.voice);
                await connection.connect();
            } catch {
                if (!live()) return;
                pendingRestore.current = null;
                stop();
                setError('Не удалось вернуться к показу экрана после обновления страницы.');
            }
        },
        [clientOptions, applyVoice, stop],
    );

    /**
     * Выбор вкладки и захват её корня (Element Capture) — общие для «Показать экран» и «Продолжить показ». Вызывать
     * прямо из нажатия: `getDisplayMedia` — до первого `await`. `not-tab` — выбрали экран или окно (§40); `gone` — показ
     * этой вкладки уже кончился или захват остановлен (поток освобождён). Отказ браузера — исключение.
     */
    const captureTab = useCallback(
        async (live: () => boolean, onTab?: () => void): Promise<ITabCapture | 'not-tab' | 'gone'> => {
            let captured: MediaStream | undefined;
            const release = () => captured?.getTracks().forEach((track) => track.stop());
            try {
                const handle = prepareSelfCapture();
                // Вызов до первого await: браузеру нужен жест пользователя.
                const stream = await navigator.mediaDevices.getDisplayMedia(CAPTURE_OPTIONS);
                captured = stream;
                // «Закончить показ» браузера в любой момент дальше, в том числе пока грузится клиент.
                stream.getVideoTracks().forEach((track) =>
                    track.addEventListener(
                        'ended',
                        () => {
                            if (live()) stop();
                        },
                        { once: true },
                    ),
                );
                const ended = () => stream.getVideoTracks().some((track) => track.readyState === 'ended');
                if (!live() || ended()) {
                    release();
                    return 'gone';
                }
                // Весь экран или окно показали бы больше кабинета (§40).
                if (!isTabCapture(stream)) {
                    release();
                    return 'not-tab';
                }
                onTab?.();
                const sharing = await import('@srm/screen-sharing');
                if (!live() || ended()) {
                    release();
                    return 'gone';
                }
                // Element Capture: в видео — только корень кабинета, оверлеи поддержки остаются у пользователя.
                const root = supportCaptureRoot();
                root.classList.add(SUPPORT_CAPTURE_CLASS);
                const restrictedToRoot = await sharing.restrictCaptureTo(stream, root);
                if (!live() || ended()) {
                    release();
                    if (live()) root.classList.remove(SUPPORT_CAPTURE_CLASS);
                    return 'gone';
                }
                if (!restrictedToRoot) root.classList.remove(SUPPORT_CAPTURE_CLASS);
                setRestricted(restrictedToRoot);
                return { stream, handle, sharing, restricted: restrictedToRoot, release };
            } catch (reason) {
                release();
                throw reason;
            }
        },
        [stop],
    );

    // Уход со страницы — приостановка, а не конец показа (§35); без показа — ничего.
    useEffect(() => {
        window.addEventListener('pagehide', suspendForUnload);
        return () => window.removeEventListener('pagehide', suspendForUnload);
    }, [suspendForUnload]);
    // Страница вернулась из кэша «назад/вперёд» уже без показа: вернуть приостановленный, как после перезагрузки.
    useEffect(() => {
        const shown = (event: PageTransitionEvent) => {
            if (!event.persisted) return;
            const saved = takeSuspendedShare(ownerRef.current ? { owner: ownerRef.current } : undefined);
            if (saved) void restore(saved);
        };
        window.addEventListener('pageshow', shown);
        return () => window.removeEventListener('pageshow', shown);
    }, [restore]);

    /**
     * Начать показ. Вызывать прямо из обработчика нажатия: выбор вкладки (`getDisplayMedia`) требует жеста
     * пользователя и запрашивается до первого `await`. `startWith` — приглашение сотрудника или номер обращения:
     * хаб сам принимает приглашение (`StartWith`), отдельного запроса не нужно. Клиент показа грузится отдельным
     * чанком уже после выбора вкладки: пока экран не показывают, его в бандле нет.
     */
    const start = useCallback(
        async (startWith?: IScreenShareStartWith) => {
            if (client.current) return;
            // Нажатие «Показать экран» разрешает странице звук: сигналы сеанса (кто подключился, микрофон).
            unlockSupportCues();
            setError('');
            setReselect(false);
            if (!navigator.mediaDevices?.getDisplayMedia || !window.RTCPeerConnection) {
                setError('Показ экрана недоступен. Откройте систему по HTTPS в настольном браузере.');
                return;
            }
            const current = ++generation.current;
            const live = () => current === generation.current;
            const startedAt = Date.now();
            setFeedback(null);
            session.current = { caseId: null, names: [], startedAt };
            knownPeers.current = new Set();
            setActive(true);
            setStatus('Выберите вкладку или окно в браузере');
            let captured: ITabCapture | undefined;
            try {
                const capture = await captureTab(live, () => setStatus('Подключаемся к поддержке…'));
                if (capture === 'gone') {
                    if (live()) stop();
                    return;
                }
                // Весь экран или окно (§40): не начинать, предложить выбрать вкладку.
                if (capture === 'not-tab') {
                    reset(false);
                    reselectWith.current = startWith;
                    setReselect(true);
                    setError(SUPPORT_TAB_ONLY_TEXT);
                    return;
                }
                captured = capture;
                const { stream, handle, sharing, restricted: restrictedToRoot } = capture;
                // Кадр показывает рабочую область при обрезке, корень — при Element Capture, иначе окно: это знает клиент.
                let connection: ScreenShareClient | null = null;
                const area = () => connection?.captureArea() ?? null;
                setCaptureArea(() => area);
                connection = new sharing.ScreenShareClient({
                    ...clientOptions(live, startedAt),
                    // Оверлеи поддержки вне видео (Element Capture): щелчок сотрудника в их месте — в страницу под ними.
                    controlHost: sharing.createTabControlHost(
                        stream,
                        handle,
                        area,
                        () => connection?.overlaysHidden ?? false,
                        offerGesture(live),
                    ),
                    startWith,
                    restrictTarget: restrictedToRoot ? supportCaptureRoot : undefined,
                });
                client.current = connection;
                setChat(connection.chat);
                applyVoice(connection.voice);
                await connection.connect(stream);
            } catch (reason) {
                captured?.release();
                if (!live()) return;
                stop();
                if (!(reason instanceof DOMException && reason.name === 'NotAllowedError'))
                    setError('Не удалось начать показ. Проверьте разрешение браузера на запись экрана.');
            }
        },
        [stop, reset, clientOptions, applyVoice, offerGesture, captureTab],
    );

    /** «Выбрать заново» в полосе отказа: вызывать прямо из нажатия — выбор вкладки требует жеста. */
    const startAgain = useCallback(() => start(reselectWith.current), [start]);

    /**
     * «Продолжить показ» после перезагрузки (§35). Вызывать прямо из нажатия: выбор вкладки требует жеста. Отмена выбора
     * оставляет показ приостановленным; сервер не ответил — тоже, с текстом под полосой.
     */
    const continueShare = useCallback(async () => {
        const connection = client.current;
        if (!connection?.suspended) return;
        unlockSupportCues();
        const current = generation.current;
        const live = () => current === generation.current && client.current === connection;
        setResumeError('');
        setResuming(true);
        try {
            const capture = await captureTab(live);
            if (capture === 'gone') return;
            // Не вкладка (§40): показ ждёт дальше, «Продолжить показ» — выбрать снова.
            if (capture === 'not-tab') {
                if (live()) setResumeError(SUPPORT_TAB_ONLY_TEXT);
                return;
            }
            const { stream, handle, sharing, restricted: restrictedToRoot } = capture;
            const area = () => connection.captureArea();
            const resumed = await connection.resumeMedia(stream, {
                controlHost: sharing.createTabControlHost(
                    stream,
                    handle,
                    area,
                    () => connection.overlaysHidden,
                    offerGesture(live),
                ),
                restrictTarget: restrictedToRoot ? supportCaptureRoot : undefined,
            });
            if (!resumed && live() && connection.suspended) {
                supportCaptureRoot().classList.remove(SUPPORT_CAPTURE_CLASS);
                setRestricted(false);
                setResumeError(SUPPORT_RESUME_FAILED_TEXT);
            }
        } catch (reason) {
            if (!live()) return;
            supportCaptureRoot().classList.remove(SUPPORT_CAPTURE_CLASS);
            // Отмена выбора вкладки — показ ждёт дальше; прочее — текст под полосой.
            if (!(reason instanceof DOMException && reason.name === 'NotAllowedError'))
                setResumeError(SUPPORT_RESUME_FAILED_TEXT);
        } finally {
            if (live()) setResuming(false);
        }
    }, [offerGesture, captureTab]);

    /** «Разрешить» в полосе: управление получает тот сотрудник, чей запрос показан (`control.peerId`). */
    const allowControl = useCallback((peerId: string | undefined) => {
        if (peerId) client.current?.allowControl(peerId);
    }, []);
    const revokeControl = useCallback(() => client.current?.revokeControl(), []);
    const dismissError = useCallback(() => {
        setError('');
        setReselect(false);
    }, []);
    /** «Запретить пометки» / «Разрешить пометки» в меню полосы (§40). */
    const setAnnotationsAllowed = useCallback((allowed: boolean) => client.current?.setAnnotationsAllowed(allowed), []);
    /** Кнопка плашки «Сотрудник просит: …» — вызывать прямо из нажатия: оно и есть жест для браузера. */
    const runGesture = useCallback(() => {
        const request = gestureRef.current;
        gestureRef.current = null;
        setGesture(null);
        request?.run();
    }, []);
    const dismissGesture = useCallback(() => {
        const request = gestureRef.current;
        gestureRef.current = null;
        setGesture(null);
        request?.dismiss();
    }, []);
    const sendCursor = useCallback(
        (x: number, y: number, visible?: boolean) => client.current?.sendCursor(x, y, visible),
        [],
    );
    const sendClickMark = useCallback((x: number, y: number) => client.current?.sendClickMark(x, y), []);
    /** Убрать пометки всех сотрудников у себя и у них (`ann-clear`). */
    const clearAnnotations = useCallback(() => client.current?.clearAnnotations(), []);

    /** `false` — не принято (показ ещё не начался на сервере): текст остаётся в поле. */
    const sendChat = useCallback((text: string) => !!client.current?.sendChat(text), []);
    /** Повтор неотправленного: ключ своего сообщения — его `clientId`. */
    const retryChat = useCallback((key: string) => client.current?.retryChat(key), []);
    const chatTyping = useCallback(() => client.current?.setTyping(), []);
    const markChatRead = useCallback(() => client.current?.markChatRead(), []);
    /** Новые сообщения специалистов (не история): объявить, пока панель чата свёрнута. Возвращает отписку. */
    const onChatIncoming = useCallback(
        (listener: (message: ISupportChatMessage) => void) => incoming.subscribe(listener),
        [incoming],
    );
    const micOn = voice.mic === 'on' || voice.mic === 'starting';
    /**
     * Микрофон: включение — прямо из нажатия (браузер спрашивает разрешение), выключение — без пересогласования.
     * Из «нет доступа» нажатие спрашивает снова: пользователь мог разрешить микрофон в настройках сайта.
     */
    const toggleMic = useCallback(async () => {
        unlockSupportCues();
        await client.current?.setMicrophone(!micOn);
    }, [micOn]);
    // Свой микрофон включился или выключился — короткий щелчок вверх или вниз (конец показа — без звука).
    const lastMic = useRef(voice.mic);
    useEffect(() => {
        const previous = lastMic.current;
        lastMic.current = voice.mic;
        if (!active || previous === voice.mic) return;
        if (voice.mic === 'on') supportCue('mic-on');
        else if (previous === 'on' && voice.mic === 'off') supportCue('mic-off');
    }, [voice.mic, active]);
    /** Браузер не дал проиграть голос специалиста без нажатия: «Включить звук». */
    const resumeVoice = useCallback(() => client.current?.resumeVoice(), []);

    // Звук (§42): выбор пользователя — клиенту на каждое изменение, громкость специалистов — по их пользователю.
    const voicePreferences = supportVoicePreferences();
    const voicePrefs = useSyncExternalStore(voicePreferences.subscribe, voicePreferences.get);
    const [devices, setDevices] = useState<IAudioDevices>(NO_AUDIO_DEVICES);
    // Названия устройств браузер отдаёт после разрешения микрофона: список — заново, когда он включился.
    const micIsOn = voice.mic === 'on';
    useEffect(() => (active ? watchAudioDevices(setDevices) : undefined), [active, micIsOn]);
    const { input, output, noiseSuppression, volumes } = voicePrefs;
    useEffect(() => {
        client.current?.setVoiceSettings(voiceSettingsOf({ input, output, noiseSuppression, volumes: {} }));
    }, [input, output, noiseSuppression]);
    useEffect(() => {
        const current = client.current;
        if (!current) return;
        for (const person of participants)
            current.setVoiceVolume(person.peerId, voiceGain(person.userId ? volumes[person.userId] : undefined));
    }, [participants, volumes]);
    const setVoiceVolume = useCallback(
        (userId: string, change: Partial<IVoiceVolume>) => {
            // Жест: общий звуковой контекст запускается — усиление выше 100 % заработает сразу.
            client.current?.resumeVoice();
            voicePreferences.setVolume(userId, change);
        },
        [voicePreferences],
    );
    const sound = useMemo<ISupportSound>(
        () => ({
            prefs: voicePrefs,
            devices,
            outputSelectable: outputSelectionSupported() && devices.outputs.length > 0,
            chooseInput: voicePreferences.setInput,
            chooseOutput: voicePreferences.setOutput,
            setNoiseSuppression: voicePreferences.setNoiseSuppression,
            volumeOf: voicePreferences.volumeOf,
            setVolume: setVoiceVolume,
        }),
        [voicePrefs, devices, voicePreferences, setVoiceVolume],
    );
    // Без ответа запрос истекает на сервере через 5 мин после первого появления — плашка уходит так же, а за ней — и
    // истёкшие в очереди.
    const diagnosticsRequestId = diagnostics?.requestId;
    const diagnosticsDeadline = diagnostics?.deadline;
    useEffect(() => {
        if (!diagnosticsRequestId) return undefined;
        const timer = setTimeout(
            () => updateDiagnostics(diagnosticsRequestId, null),
            Math.max(0, (diagnosticsDeadline ?? Date.now() + DIAGNOSTICS_REQUEST_TTL) - Date.now()),
        );
        return () => clearTimeout(timer);
    }, [diagnosticsRequestId, diagnosticsDeadline, updateDiagnostics]);
    // Автоотметки ошибок вкладки (§44.2) — специалистам, пока идёт показ.
    useEffect(() => (active ? onSupportIssue((issue) => client.current?.reportIssue(issue)) : undefined), [active]);
    const shareInfoRef = useRef({ transport, quality, viewers: participants.length });
    shareInfoRef.current = { transport, quality, viewers: participants.length };
    /** Пакет диагностики сейчас — для «Посмотреть, что уйдёт» и для отправки. Без стора (тесты) — `null`. */
    const previewDiagnostics = useCallback((): ISupportDiagnostics | null => {
        const state = readStateRef.current?.();
        if (!state) return null;
        const share = shareInfoRef.current;
        return collectSupportDiagnostics(state, {
            transport: share.transport,
            ...(share.quality ? { quality: share.quality } : {}),
            viewers: share.viewers,
        });
    }, []);
    /**
     * «Отправить» (§43.1): согласие — сам `POST` пакета; `shown` — пакет, который пользователь посмотрел: уходит ровно
     * он. Повтор после отказа сети — то же нажатие.
     */
    const sendDiagnostics = useCallback(
        async (shown?: ISupportDiagnostics) => {
            const ask = diagnosticsRef.current;
            if (!ask || ask.phase === 'sending') return;
            const live = () => diagnosticsRef.current?.requestId === ask.requestId;
            updateDiagnostics(ask.requestId, { ...ask, phase: 'sending', error: undefined });
            try {
                const body = shown ?? previewDiagnostics();
                if (!body) throw new Error('no state');
                await $api.post(diagnosticsPath(ask.shareId, ask.requestId), body, {
                    suppressErrorNotification: true,
                    skipSupportErrorLog: true,
                });
                void client.current?.answerDiagnostics(ask.requestId, true);
                updateDiagnostics(ask.requestId, null);
                sendNotification({ message: SUPPORT_DIAGNOSTICS_SENT_TEXT, variant: 'green' });
            } catch (reason) {
                const status = httpStatus(reason);
                if (status === 404 || status === 409) {
                    void client.current?.answerDiagnostics(ask.requestId, true);
                    updateDiagnostics(ask.requestId, null);
                    sendNotification({ message: SUPPORT_DIAGNOSTICS_STALE_TEXT, variant: 'yellow' });
                    return;
                }
                if (live())
                    updateDiagnostics(ask.requestId, {
                        ...ask,
                        phase: 'failed',
                        error:
                            (status === 403 || status === 429 ? httpMessage(reason) : undefined) ??
                            SUPPORT_DIAGNOSTICS_FAILED_TEXT,
                    });
            }
        },
        [previewDiagnostics, updateDiagnostics],
    );
    /** «Не отправлять» — отказ в хаб (`AnswerDiagnostics`). */
    const declineDiagnostics = useCallback(() => {
        const ask = diagnosticsRef.current;
        if (!ask) return;
        updateDiagnostics(ask.requestId, null);
        void client.current?.answerDiagnostics(ask.requestId, false);
    }, [updateDiagnostics]);
    const dismissFeedback = useCallback(() => setFeedback(null), []);
    /** Пользователь выбрал оценку: плашка ждёт его, а не уходит по таймеру. */
    const holdFeedback = useCallback(
        () => setFeedback((offer) => (offer && !offer.held ? { ...offer, held: true } : offer)),
        [],
    );
    const caseId = feedback?.caseId;
    /**
     * Отправить оценку; отказ — исключение для плашки. «Уже оценено» (другая вкладка) — успех; «показ ещё не закрыт»
     * (оценку выбрали сразу после «Остановить показ», `Leave` ещё в пути) — повтор через полторы секунды.
     */
    const sendFeedback = useCallback(
        async (rating: SupportFeedbackRating, comment: string) => {
            if (!caseId) return;
            const body = { caseId, rating, comment: comment.trim() || null };
            for (let attempt = 0; ; attempt++) {
                try {
                    await SupportAccessService.sendFeedback(body);
                    break;
                } catch (reason) {
                    const rejection = feedbackRejection(reason);
                    if (rejection === 'already_rated') break;
                    if (rejection === 'expired') throw new SupportFeedbackError(SUPPORT_FEEDBACK_EXPIRED_TEXT);
                    if (rejection !== 'not_ended' || attempt >= SUPPORT_FEEDBACK_RETRIES) throw reason;
                    await new Promise((resume) => setTimeout(resume, SUPPORT_FEEDBACK_RETRY_DELAY));
                }
            }
            setFeedback((offer) => (offer?.caseId === caseId ? { ...offer, sent: true } : offer));
        },
        [caseId],
    );
    useEffect(() => {
        if (!feedback || (feedback.held && !feedback.sent)) return undefined;
        const timer = setTimeout(
            () => setFeedback(null),
            feedback.sent ? SUPPORT_FEEDBACK_THANKS_TIMEOUT : SUPPORT_FEEDBACK_TIMEOUT,
        );
        return () => clearTimeout(timer);
    }, [feedback]);

    return useMemo(() => {
        const state: IScreenShareState = {
            active,
            status,
            error,
            control,
            connection,
            viewerName,
            participants,
            annotated,
            actions,
            quality,
            restricted,
            cropped,
            transport,
            captureArea,
            chat,
            voice,
            feedback,
            suspended,
            resuming,
            restoring,
            resumeError,
            reselect,
            annotationsAllowed,
            gesture,
            diagnostics,
        };
        return {
            ...state,
            micLevel,
            speaking,
            presence,
            start,
            startAgain,
            restore,
            continueShare,
            stop,
            allowControl,
            revokeControl,
            dismissError,
            setAnnotationsAllowed,
            runGesture,
            dismissGesture,
            sendCursor,
            sendClickMark,
            clearAnnotations,
            sendChat,
            retryChat,
            chatTyping,
            markChatRead,
            onChatIncoming,
            toggleMic,
            resumeVoice,
            sound,
            dismissFeedback,
            holdFeedback,
            sendFeedback,
            previewDiagnostics,
            sendDiagnostics,
            declineDiagnostics,
        };
    }, [
        active,
        status,
        error,
        control,
        connection,
        viewerName,
        participants,
        annotated,
        actions,
        quality,
        restricted,
        cropped,
        transport,
        captureArea,
        chat,
        voice,
        feedback,
        suspended,
        resuming,
        restoring,
        resumeError,
        reselect,
        annotationsAllowed,
        gesture,
        diagnostics,
        micLevel,
        speaking,
        presence,
        start,
        startAgain,
        restore,
        continueShare,
        stop,
        allowControl,
        revokeControl,
        dismissError,
        setAnnotationsAllowed,
        runGesture,
        dismissGesture,
        sendCursor,
        sendClickMark,
        clearAnnotations,
        sendChat,
        retryChat,
        chatTyping,
        markChatRead,
        onChatIncoming,
        toggleMic,
        resumeVoice,
        sound,
        dismissFeedback,
        holdFeedback,
        sendFeedback,
        previewDiagnostics,
        sendDiagnostics,
        declineDiagnostics,
    ]);
}

export type ScreenShareApi = ReturnType<typeof useScreenShare>;
